<template>
  <div class="summary-pills-wrapper">
    <div class="summary-inputs">
      <span class="summary-pill pill-colaboradores">
        <span class="summary-pill-text">{{ filteredTableData.length }} Colaboradores</span>
      </span>
      <span class="summary-pill pill-agente">
        <span class="summary-pill-text">Agente: {{ selectAgentDescription }}</span>
      </span>
      <span class="summary-pill admin-pill pill-admin" v-if="firstAdministrator">
        <span class="summary-pill-text">Admin: {{ firstAdministrator }}</span>
        <div
          class="admin-counter"
          v-if="remainingAdminsCount > 0"
          @mouseover="handleMouseOver"
          @mouseleave="handleMouseLeave"
          ref="counterRef"
        >
          <span class="counter-text">+{{ remainingAdminsCount }}</span>
        </div>
      </span>
    </div>

    <!-- Popover for showing all administrator emails -->
    <teleport to="body">
      <PopoverMessage
        v-if="showPopover && remainingAdminsCount > 0 && allAdministratorEmails.length > 0"
        :messages="allAdministratorEmails"
        :top="popoverTop"
        :left="popoverLeft"
        ref="popoverRef"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, onMounted, nextTick } from 'vue'
import { useBulkLoadStore } from '../stores/bulkLoad'
import type { AgentDTO } from 'commons'
import type { MembersData } from '../../../commons/src/data-validator/dto/validated-data'
import { ApiService } from '../services/apiService'
import type { CompanyAdministratorEmailsDto } from 'commons'
import PopoverMessage from './PopoverMessage.vue'

interface Props {
  filteredTableData: MembersData[]
  availableAgents: AgentDTO[]
  selectedAgent: string
}

const props = defineProps<Props>()
const bulkLoadStore = useBulkLoadStore()
const apiService = inject<ApiService>('apiService')

const administrators = ref<CompanyAdministratorEmailsDto | null>(null)
const showPopover = ref(false)
const counterRef = ref<HTMLElement | null>(null)
const popoverRef = ref<unknown>(null)
const popoverTop = ref(0)
const popoverLeft = ref(0)

/* const selectedGroupDescription = computed(() => {
  const nombrePublico = bulkLoadStore.publicGroupName
  const descripcion = bulkLoadStore.selectedGroup?.descripcion
  const nombrePublicoText = nombrePublico && nombrePublico !== '' ? ` (${nombrePublico})` : ''
  return `${descripcion}${nombrePublicoText}`
}) */

const selectAgentDescription = computed(() => {
  const selected = props.availableAgents.find(
    (agent: AgentDTO) => agent.userName === bulkLoadStore.selectedAgent,
  )
  if (selected) {
    return `${selected.nombre} ${selected.apPaterno} ${selected.apMaterno}`
  }
  return 'No asignado'
})

const firstAdministrator = computed(() => {
  if (!administrators.value?.emails?.length) return null
  const sortedAdmins = [...administrators.value.emails].sort((a, b) => a.order - b.order)
  return sortedAdmins[0]?.email || null
})

const remainingAdminsCount = computed(() => {
  if (!administrators.value?.emails?.length) return 0
  return Math.max(0, administrators.value.emails.length - 1)
})

const allAdministratorEmails = computed(() => {
  if (!administrators.value?.emails?.length) return []
  const sortedAdmins = [...administrators.value.emails].sort((a, b) => a.order - b.order)
  return sortedAdmins.map((admin) => admin.email)
})

const handleMouseOver = () => {
  showPopover.value = true
  nextTick(() => {
    if (
      showPopover.value &&
      counterRef.value &&
      popoverRef.value &&
      (popoverRef.value as { popoverRef: HTMLElement }).popoverRef
    ) {
      const counterRect = counterRef.value.getBoundingClientRect()
      const popoverRect = (
        popoverRef.value as { popoverRef: HTMLElement }
      ).popoverRef.getBoundingClientRect()
      popoverTop.value = counterRect.top - popoverRect.height - 10 // 10px de espacio
      popoverLeft.value = counterRect.left + counterRect.width / 2 - 110 // Center the popover
    }
  })
}

const handleMouseLeave = () => {
  showPopover.value = false
}

const loadAdministrators = async () => {
  if (!apiService || !bulkLoadStore.selectedGroup?.id) return

  try {
    administrators.value = await apiService.getCompanyAdministrators(bulkLoadStore.selectedGroup.id)
    bulkLoadStore.setCompanyAdministrators(administrators.value)
  } catch (error) {
    console.error('Hubo un error: Error loading administrators ', error)
  }
}

onMounted(() => {
  loadAdministrators()
})

// Expose refresh method for parent components
const refreshAdministrators = async () => {
  await loadAdministrators()
}

defineExpose({
  refreshAdministrators,
})
</script>

<style scoped>
.summary-pills-wrapper {
  flex: 0 0 auto;
  align-self: flex-start;
}

.summary-inputs {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 8px;
  margin: 0 auto;
  width: 1231px;
  height: 42px;
  flex: none;
  order: 0;
  flex-grow: 1;
}

.summary-pill {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 11px 16px;
  gap: 8px;
  height: 42px;
  background: #f7fbff;
  border: 1px solid #e4e4e4;
  border-radius: 4px;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
  white-space: nowrap;
}

.pill-colaboradores {
  min-width: 156px;
  order: 0;
}

.pill-agente {
  min-width: 158px;
  order: 1;
}

.pill-admin {
  min-width: 308px;
  order: 2;
  gap: 8px;
}

.summary-pill-text {
  width: auto;
  height: 20px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 145%;
  display: flex;
  align-items: center;
  color: #a1a1a1;
  flex: none;
  order: 0;
  flex-grow: 0;
  white-space: nowrap;
}

/* Group pill specific styling */
.group-pill-row {
  margin-bottom: 8px;
}

.group-pill {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 24px;
  gap: 15px;
  width: 618px;
  height: 30px;
  background: #003896;
  border: 1px solid #e4e4e4;
  border-radius: 500px;
  flex: none;
  order: 0;
  flex-grow: 0;
}

.group-pill-text {
  width: 570px;
  height: 18px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 130%;
  display: flex;
  align-items: center;
  color: #fafafa;
  flex: none;
  order: 0;
  flex-grow: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Admin pill specific styling */
.admin-pill {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.admin-counter {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4px;
  gap: 15px;
  width: 26px;
  height: 28px;
  background: #003896;
  border: 1px solid #91c9ff;
  border-radius: 22222px;
  flex: none;
  order: 1;
  flex-grow: 0;
}

.counter-text {
  width: 18px;
  height: 20px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 145%;
  display: flex;
  align-items: center;
  color: #fafafa;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}
</style>
